import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSkeleton } from '../DashboardLoader';
import {
  Utensils,
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Star,
  Clock,
  DollarSign,
  Users,
  ChefHat,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreVertical,
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin
} from 'lucide-react';
import { getAllDishes, deleteDish, suspendDish, formatCurrency } from '../../../services/adminApi';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { RatingStarsFa, getDisplayRating } from '../../../utils/ratingStars';
import namkeFallback from '../../../assets/namke-fallback.png';

const statIconWrap = 'p-3 rounded-full bg-gray-100 flex-shrink-0';
const statIconClass = 'h-6 w-6 text-gray-600';

const AdminDishes = () => {
  const navigate = useNavigate();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    restaurant: '',
    available: true,
    featured: false
  });

  const statusOptions = [
    { id: 'all', name: 'Tous les plats' },
    { id: 'available', name: 'Disponibles' },
    { id: 'unavailable', name: 'Indisponibles' },
    { id: 'featured', name: 'Mis en avant' }
  ];

  const categoryOptions = [
    { id: 'all', name: 'Toutes les catégories' },
    { id: 'appetizer', name: 'Entrées' },
    { id: 'main', name: 'Plats principaux' },
    { id: 'dessert', name: 'Desserts' },
    { id: 'beverage', name: 'Boissons' },
    { id: 'side', name: 'Accompagnements' }
  ];

  useEffect(() => {
    loadDishes();
  }, []);

  const loadDishes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllDishes();
      setDishes(data);
    } catch (error) {
      console.error('Erreur lors du chargement des plats:', error);
      setError('Erreur lors du chargement des plats');
      setDishes([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredDishes = dishes.filter(dish => {
    const desc = (dish.description && String(dish.description)) || '';
    const restLabel =
      dish.restaurant != null && typeof dish.restaurant === 'object'
        ? String(dish.restaurant.name || dish.restaurant.id || '')
        : String(dish.restaurant || '');
    const matchesSearch = dish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restLabel.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'available' && dish.available) ||
                         (filterStatus === 'unavailable' && !dish.available) ||
                         (filterStatus === 'featured' && dish.featured);
    const matchesCategory = filterCategory === 'all' || dish.category === filterCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const ratingValues = dishes
    .map((d) => getDisplayRating(d, 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  const avgRatingDisplay =
    ratingValues.length > 0
      ? (ratingValues.reduce((a, b) => a + b, 0) / ratingValues.length).toFixed(1)
      : '0.0';

  const stats = [
    {
      title: 'Total des Plats',
      value: dishes.length.toString(),
      change: '+12%',
      icon: Utensils,
    },
    {
      title: 'Plats Disponibles',
      value: dishes.filter(d => d.available).length.toString(),
      change: '+8%',
      icon: CheckCircle,
    },
    {
      title: 'Plats Mis en Avant',
      value: dishes.filter(d => d.featured).length.toString(),
      change: '+15%',
      icon: Star,
    },
    {
      title: 'Note Moyenne',
      value: avgRatingDisplay,
      change: '+0.2',
      icon: TrendingUp,
    }
  ];

  const getCategoryName = (category) => {
    if (category != null && typeof category === 'object' && 'name' in category) return category.name;
    const cat = categoryOptions.find(c => c.id === category);
    return cat ? cat.name : (typeof category === 'string' ? category : '—');
  };

  const getCategoryIcon = (category) => {
    const categoryId = category != null && typeof category === 'object' && 'id' in category ? category.id : category;
    const cls = 'w-4 h-4 text-gray-500';
    switch (categoryId) {
      case 'appetizer': return <ChefHat className={cls} />;
      case 'main': return <Utensils className={cls} />;
      case 'dessert': return <Star className={cls} />;
      case 'beverage': return <Users className={cls} />;
      case 'side': return <ChefHat className={cls} />;
      default: return <Utensils className={cls} />;
    }
  };

  const getStatusBadge = (dish) => {
    if (dish.featured) {
      return <span className="px-2 py-1 text-xs font-medium text-white rounded-full" style={{ backgroundColor: 'rgb(5, 70, 37)' }}>Mis en avant</span>;
    }
    if (dish.available) {
      return <span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">Disponible</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">Indisponible</span>;
  };

  const handleCreateDish = () => {
    console.log('Créer plat:', formData);
    setShowCreateModal(false);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      restaurant: '',
      available: true,
      featured: false
    });
  };

  const handleEditDish = (dish) => {
    setSelectedDish(dish);
    setFormData(dish);
    setShowEditModal(true);
  };

  const handleUpdateDish = () => {
    console.log('Mettre à jour plat:', formData);
    setShowEditModal(false);
  };

  const handleDeleteDish = async (id) => {
    try {
      await deleteDish(id);
      setDishes(dishes.filter(dish => dish.id !== id));
      setShowDeleteModal(false);
      setSelectedDish(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression du plat');
    }
  };

  const handleSuspendDish = async (id) => {
    try {
      await suspendDish(id);
      setDishes(dishes.map(dish => 
        dish.id === id ? { ...dish, available: !dish.available } : dish
      ));
    } catch (error) {
      console.error('Erreur lors de la suspension:', error);
      setError('Erreur lors de la suspension du plat');
    }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Plats</h1>
        <p className="text-gray-600">Gérez tous les plats de la plateforme</p>
      </div>

      {/* Stats Cards - style comme management (icônes colorées) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          const displayValue = typeof stat.value === 'object' ? (stat.value?.name ?? stat.value?.id ?? '—') : stat.value;
          return (
            <div key={index} className="bg-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{displayValue}</p>
                  <p className="text-sm text-gray-500">{stat.change}</p>
                </div>
                <div className={statIconWrap}>
                  <IconComponent className={statIconClass} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un plat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              {statusOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <ChefHat className="w-4 h-4 text-gray-600" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              {categoryOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
            style={{ backgroundColor: 'rgb(5, 70, 37)' }}
          >
            <Plus className="w-4 h-4 text-white" />
            Nouveau Plat
          </button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            {error?.message || error?.toString() || 'Une erreur est survenue'}
          </div>
        )}
      </div>

      {/* Dishes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDishes.map((dish) => {
          const dishImage =
            resolveMediaUrl(dish.image_url) ||
            resolveMediaUrl(dish.dish_image_url) ||
            resolveMediaUrl(dish.image) ||
            resolveMediaUrl(dish.photo_url);
          const displayRating = getDisplayRating(dish, 0);
          return (
          <div key={dish.id} className="bg-white rounded-lg overflow-hidden transition-shadow">
            {/* Dish Image */}
            <div className="h-48 bg-gray-200 relative overflow-hidden">
              <img
                src={dishImage || namkeFallback}
                alt={dish.name || 'Plat'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = namkeFallback;
                }}
              />
              <div className="absolute top-4 left-4">
                {getStatusBadge(dish)}
              </div>
              <div className="absolute top-4 right-4">
                <button type="button" className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100">
                  <MoreVertical className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="absolute bottom-4 right-4">
                <div className="flex items-center gap-1 bg-white/95 px-2 py-1 rounded">
                  <RatingStarsFa rating={displayRating} size="sm" />
                  <span className="text-sm font-medium text-gray-900 tabular-nums">
                    {displayRating > 0 ? displayRating.toFixed(1) : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Dish Info */}
    <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{dish.name}</h3>
                  <div className="flex items-center space-x-1">
                    {getCategoryIcon(dish.category)}
                    <span className="text-xs text-gray-500">{getCategoryName(dish.category)}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">{dish.description || '—'}</p>
                
                {/* Price and Restaurant */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <DollarSign className="w-4 h-4 mr-1 text-gray-500" />
                    <span className="font-medium">{formatCurrency(dish.price || 0)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-1 text-gray-500" />
                    <span className="truncate max-w-32">
                      {dish.restaurant != null && typeof dish.restaurant === 'object'
                        ? (dish.restaurant.name ?? `#${dish.restaurant.id ?? ''}`)
                        : (dish.restaurant ?? '—')}
                    </span>
                  </div>
                </div>

                {/* Preparation Time */}
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4 mr-1 text-gray-500" />
                  <span>Préparation: {dish.preparationTime}</span>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <RatingStarsFa rating={displayRating} size="sm" />
                    <span>
                      {displayRating > 0 ? displayRating.toFixed(1) : '—'} (
                      {dish.review_count ?? dish.reviews ?? 0} avis)
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="w-4 h-4 mr-1" />
                    {dish.orders} commandes
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-1">
                  {(() => {
                    const rid = dish.restaurant_id ?? (dish.restaurant != null && typeof dish.restaurant === 'object' ? dish.restaurant.id : null);
                    return (
                      <button
                        type="button"
                        title={rid ? 'Voir le restaurant' : 'Restaurant inconnu'}
                        disabled={!rid}
                        onClick={() => rid && navigate(`/restaurant/${rid}`)}
                        className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    );
                  })()}
                  <button 
                    type="button"
                    title="Modifier"
                    onClick={() => handleEditDish(dish)}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    type="button"
                    title="Supprimer"
                    onClick={() => {
                      setSelectedDish(dish);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  ID: {dish.id}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {filteredDishes.length === 0 && (
        <div className="text-center py-12">
          <Utensils className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun plat trouvé</h3>
          <p className="text-gray-500">Aucun plat ne correspond à vos critères de recherche</p>
        </div>
      )}

      {/* Create Dish Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nouveau Plat</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du plat</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner</option>
                    {categoryOptions.filter(c => c.id !== 'all').map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateDish}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: 'rgb(5, 70, 37)' }}
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dish Modal */}
      {showEditModal && selectedDish && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Modifier le Plat</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du plat</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Disponible</label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.available}
                    onChange={(e) => setFormData({...formData, available: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateDish}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: 'rgb(5, 70, 37)' }}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDish && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900 ml-2">Supprimer le plat</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le plat "{selectedDish.name}" ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteDish(selectedDish.id)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDishes;




